import { useEffect, useRef, useState } from "react";

type Documents = {
  version: string;
  fingerprint: string;
  terms_text: string;
  fair_use_text: string;
  checkout_enabled: boolean;
  privacy_consent?: { version: string; text: string };
  declarations: { terms: string; age: string; immediate_access: string };
};

export default function MembershipAgreementForm({ plan, onCancel }: {
  plan: "monthly" | "annual";
  onCancel: () => void;
}) {
  const [documents, setDocuments] = useState<Documents | null>(null);
  const [terms, setTerms] = useState(false);
  const [sensitiveConsent, setSensitiveConsent] = useState(false);
  const [age, setAge] = useState(false);
  const [immediate, setImmediate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const heading = useRef<HTMLHeadingElement>(null);
  const inFlight = useRef(false);
  useEffect(() => {
    heading.current?.focus();
    let current = true;
    fetch("/memberships/checkout/documents", { cache: "no-store" })
      .then(async response => {
        if (!response.ok) throw new Error("We could not load the membership documents. Please try again.");
        return response.json();
      })
      .then(data => { if (current) setDocuments(data); })
      .catch(reason => { if (current) setError(reason.message); });
    return () => { current = false; };
  }, []);

  async function continueToPayment(event: React.FormEvent) {
    event.preventDefault();
    if (!documents || !terms || !age || !immediate || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/memberships/checkout/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_interval: plan, document_fingerprint: documents.fingerprint,
          terms_accepted: terms, age_confirmed: age, immediate_access_requested: immediate,
          sensitive_data_consent: sensitiveConsent,
          sensitive_consent_version: documents.privacy_consent?.version ?? null,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.detail === "string" ? data.detail : "We could not open checkout. Please try again.");
      const url = new URL(data.checkout_url);
      if (url.protocol !== "https:" || url.hostname !== "checkout.stripe.com") throw new Error("We could not verify the secure checkout address.");
      window.location.assign(url.href);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Please try again.");
      setBusy(false);
      inFlight.current = false;
    }
  }

  return <section className="membership-plan" aria-labelledby="membership-agreement-heading">
    <h3 ref={heading} tabIndex={-1} id="membership-agreement-heading">Before you pay</h3>
    <p>{plan === "monthly" ? "£30 per month, renewing automatically each month unless cancelled." : "£299 per year, renewing automatically each year unless cancelled. Enter FIRST50 at Stripe checkout for £50 off the first year when available."}</p>
    <p>Read the Terms and Conditions and Fair Use Policy below, then tick the boxes to confirm your agreement. You do not need to download anything. A copy of the documents you accept will be emailed to you after payment.</p>
    {error && <p role="alert">{error}</p>}
    {!documents && !error && <p role="status">Loading membership documents…</p>}
    {documents && <form onSubmit={continueToPayment}>
      <p><a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">Read the Privacy Policy</a></p>
      <p>Document version: {documents.version}</p>
      <details><summary>Read membership terms</summary><pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{documents.terms_text}</pre></details>
      <details><summary>Read Fair Use Policy</summary><pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{documents.fair_use_text}</pre></details>
      <p><label><input type="checkbox" required checked={terms} disabled={busy} onChange={e => setTerms(e.target.checked)} /> {documents.declarations.terms}</label></p>
      <p><label><input type="checkbox" required checked={age} disabled={busy} onChange={e => setAge(e.target.checked)} /> {documents.declarations.age}</label></p>
      <p><label><input type="checkbox" required checked={immediate} disabled={busy} onChange={e => setImmediate(e.target.checked)} /> {documents.declarations.immediate_access}</label></p>
      {documents.privacy_consent && <fieldset>
        <legend>Personal workspace privacy consent</legend>
        <p>You can join without giving this consent. New journal entries, check-ins, personal plans and AI requests remain paused until you give it. You can withdraw it in Sanctuary. Withdrawal does not stop subscription renewal; cancellation remains a separate choice.</p>
        <label><input type="checkbox" checked={sensitiveConsent} disabled={busy} onChange={e => setSensitiveConsent(e.target.checked)} /> {documents.privacy_consent.text}</label>
        <p>CBC does not sell your personal information or share journals or conversations for third-party marketing.</p>
      </fieldset>}
      {!documents.checkout_enabled && <p>Membership joining opens shortly.</p>}
      <button className="entry-button" type="submit" disabled={busy || !terms || !age || !immediate || !documents.checkout_enabled}>{busy ? "Opening secure checkout…" : "Continue to secure payment"}</button>
    </form>}
    <button type="button" onClick={onCancel} disabled={busy}>Back to membership options</button>
  </section>;
}
